import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'landing',
      component: () => import('../views/LandingPage.vue'),
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginPage.vue'),
      meta: { guestOnly: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/RegisterPage.vue'),
      meta: { guestOnly: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('../views/AuthCallbackPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/auth/accept-legal',
      name: 'accept-legal',
      component: () => import('../views/AcceptLegalPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import('../views/CheckoutPage.vue'),
      meta: { requiresAuth: true, requiresVerified: true },
    },
    {
      path: '/checkout/product',
      name: 'product-checkout',
      component: () => import('../views/ProductCheckoutPage.vue'),
      // Auth is bootstrapped from ?token= query param inside the component.
      // No router-level auth gate so the redirect flow works without a
      // pre-existing session on the payment-service domain.
      meta: { requiresAuth: false },
    },
    {
      path: '/checkout/success',
      name: 'checkout-success',
      component: () => import('../views/SuccessPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/checkout/cancelled',
      name: 'checkout-cancelled',
      component: () => import('../views/CancelledPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/DashboardPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('../views/SettingsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/terms',
      name: 'terms',
      redirect: '/',
      beforeEnter: () => {
        window.location.href = 'https://plutos.trading/legal/terms';
        return false;
      },
    },
    {
      path: '/privacy',
      name: 'privacy',
      redirect: '/',
      beforeEnter: () => {
        window.location.href = 'https://plutos.trading/legal/privacy';
        return false;
      },
    },
    {
      path: '/support',
      name: 'support',
      redirect: '/',
      beforeEnter: () => {
        window.location.href = 'https://plutos.trading/support';
        return false;
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundPage.vue'),
    },
  ],
});

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();
  
  // Wait for auth to initialize
  if (!authStore.initialized) {
    await authStore.initialize();
  }
  
  const isAuthenticated = !!authStore.user;
  const isVerified = authStore.isVerified;
  
  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }
  
  if (to.meta.requiresVerified && !isVerified) {
    next({ name: 'dashboard' }); // Dashboard will show verification prompt
    return;
  }
  
  if (to.meta.guestOnly && isAuthenticated) {
    next({ name: 'dashboard' });
    return;
  }
  
  next();
});

export default router;
